import { Component, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthService } from '@app/services/auth.service';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RegisterLoginComponent } from '../register-login/register-login.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { DownloadLinksComponent } from '@app/components/download-links/download-links.component';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from '@app/services/user.service';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    DownloadLinksComponent,
    MatIconModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
fb = inject(FormBuilder);
  state: 'initial' | 'verify' | 'reset' = 'initial';
  authService = inject(AuthService);
  form: FormGroup;
  otpForm: FormGroup;
  resetForm: FormGroup;
  isLoading = signal(false);
  successMessage = '';
  errorMessage = '';
  isResendButtonDisabled = signal(false);
  remainingTime = 0;
  formattedTime = signal('00:00');
  userId: string;
  userToken: string;
  dialogRef = inject(MatDialogRef<RegisterLoginComponent>);

  constructor(
    private dialog: MatDialog,
    private userService: UserService,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.otpForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
    });
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(12)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(12)]],
    });
    dayjs.extend(duration);
  }

  sendVerificationCode() {
    if (this.form.invalid) {
      return;
    }

    this.isLoading.set(true);

    const { email } = this.form.value;

    this.userService.sendResetPasswordEmail(email).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.state = 'verify';
        this.successMessage = 'Check your email for the OTP';
        this.errorMessage = '';
        this.disableResendButtonForTwoMinutes();
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage = 'Something went wrong';
        this.successMessage = '';
      },
    });
  }

  resendVerificationCode() {
    this.sendVerificationCode();
    this.disableResendButtonForTwoMinutes();
  }

  disableResendButtonForTwoMinutes() {
    this.isResendButtonDisabled.set(true);

    // Set remaining time to 120 seconds (2 minutes)
    this.remainingTime = 120;
    this.formattedTime.set(dayjs.duration(this.remainingTime, 'seconds').format('mm:ss'));

    const intervalId = setInterval(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
        this.formattedTime.set(dayjs.duration(this.remainingTime, 'seconds').format('mm:ss'));
      } else {
        // Enable button and clear the interval when time is up
        this.isResendButtonDisabled.set(false);
        clearInterval(intervalId);
      }
    }, 1000);
  }

  onSubmit(event: Event) {
    event.preventDefault();
    this.sendVerificationCode();
    this.successMessage = '';
    this.errorMessage = '';
  }

  onOtpSubmit(event: Event) {
    event.preventDefault();
    this.successMessage = '';
    this.errorMessage = '';

    if (this.otpForm.invalid) {
      return;
    }
    this.isLoading.set(true);

    const { code } = this.otpForm.value;

    this.userService.verifyResetPassword(this.form.value.email, code).subscribe({
      next: (data: any) => {
        this.isLoading.set(false);
        this.state = 'reset';
        this.successMessage = 'Check your email for the OTP';
        this.errorMessage = '';
        this.userId = data?.userId;
        this.userToken = data?.token;
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage = 'Something went wrong';
        this.successMessage = '';
      },
    });
  }

  async onResetSubmit(event: Event) {
    event.preventDefault();
    this.successMessage = '';
    this.errorMessage = '';

    if (this.resetForm.invalid) {
      return;
    }
    this.isLoading.set(true);

    const { password, confirmPassword } = this.resetForm.value;

    if (password !== confirmPassword) {
      this.isLoading.set(false);
      this.errorMessage = 'Passwords do not match';
      return;
    }

    await firstValueFrom(this.authService.loginWithToken(this.userToken));
    const userToken = await firstValueFrom(this.authService.getIdToken());

    this.userService.changePassword(this.userId, userToken, password).subscribe({
      next: () => {
        this.dialogRef.close();
      },
      error: () => {
        this.isLoading.set(false);
        this.errorMessage = 'Something went wrong';
        this.successMessage = '';
      },
    });
  }

  openSignInForm() {
    this.dialogRef.close();
    this.dialog.open(RegisterLoginComponent, {
      maxWidth: '1000px',
      data: {
        type: 'login',
      },
    });
  }
}
